import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  useMediaQuery
} from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { SHOPIFY_STORE_URL, SHOPIFY_ACCESS_TOKEN } from './config';
import Home from './components/pages/Home';
import CampDetails from './components/pages/CampDetails';
import MobileCampView from './components/pages/MobileCampView';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#4caf50',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          textTransform: 'none',
          fontWeight: 500,
          padding: '6px 20px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  },
});

// Availability thresholds
const LOW_STOCK_THRESHOLD = 5;

function App() {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [locations, setLocations] = useState([]);

  const fetchCamps = async () => {
    setLoading(true);
    setError(null);
    try {
      const storeUrl = SHOPIFY_STORE_URL;
      const accessToken = SHOPIFY_ACCESS_TOKEN;

      // Fetch all variants (up to 50 per product) to get per-week inventory
      const graphqlQuery = `
        query {
          products(first: 50) {
            edges {
              node {
                id
                title
                description
                tags
                handle
                featuredImage {
                  url
                  altText
                }
                metafield(namespace: "custom", key: "week_availability") {
                  value
                }
                shortDescriptionMetafield: metafield(namespace: "custom", key: "short_description") {
                  value
                }
                options {
                  name
                  values
                }
                variants(first: 50) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                      }
                      selectedOptions {
                        name
                        value
                      }
                      quantityAvailable
                      availableForSale
                      currentlyNotInStock
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await axios({
        url: `https://${storeUrl}/api/2024-01/graphql.json`,
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Content-Type': 'application/json',
        },
        data: { query: graphqlQuery }
      });

      if (response.data.errors) {
        throw new Error(`GraphQL Error: ${response.data.errors[0].message}`);
      }

      const products = response.data.data.products.edges
        .map(edge => {
          const node = edge.node;

          let weekAvailability = [];
          let shortDescription = '';
          try {
            if (node.metafield?.value) {
              const metafieldData = JSON.parse(node.metafield.value);
              weekAvailability = metafieldData.weeks || [];
            }
            if (node.shortDescriptionMetafield?.value) {
              shortDescription = node.shortDescriptionMetafield.value;
            }
          } catch (e) {
            console.error('Error parsing metafield for:', node.title, e);
          }

          // Process all variants to get per-week, per-location inventory
          const variants = node.variants.edges.map(v => {
            const variant = v.node;
            const options = {};
            variant.selectedOptions.forEach(opt => {
              options[opt.name.toLowerCase()] = opt.value;
            });

            return {
              id: variant.id,
              title: variant.title,
              price: variant.price.amount,
              location: options['location'] || null,
              dates: options['dates'] || options['date'] || options['week'] || null,
              quantityAvailable: variant.quantityAvailable,
              availableForSale: variant.availableForSale,
              currentlyNotInStock: variant.currentlyNotInStock,
              options
            };
          });

          // Build per-week availability from variant inventory data
          const weekSlots = variants
            .filter(variant => variant.dates) // only include variants with a valid dates option
            .map(variant => {
              let status = 'available';
              let spotsLeft = variant.quantityAvailable;

              // Determine sold out: not available for sale, out of stock, or 0 quantity
              if (!variant.availableForSale || variant.currentlyNotInStock) {
                status = 'sold_out';
              } else if (spotsLeft === 0) {
                status = 'sold_out';
              } else if (spotsLeft !== null && spotsLeft < LOW_STOCK_THRESHOLD) {
                status = 'low';
              }
              // If spotsLeft is null but availableForSale is true, treat as available
              // (inventory quantity not exposed by API but variant is purchasable)

              return {
                variantId: variant.id,
                location: variant.location,
                dates: variant.dates,
                price: variant.price,
                quantityAvailable: spotsLeft,
                availableForSale: variant.availableForSale,
                status // 'available', 'low', 'sold_out'
              };
            });

          // Fallback: use product options to fill in any missing date/location combos
          // This ensures all weeks show even if the Storefront API doesn't return variant details
          const optionLocations = node.options.find(o => o.name.toLowerCase() === 'location')?.values || [];
          const optionDates = node.options.find(o => o.name.toLowerCase() === 'dates' || o.name.toLowerCase() === 'date' || o.name.toLowerCase() === 'week')?.values || [];
          const defaultPrice = variants[0]?.price || '0';

          optionLocations.forEach(loc => {
            optionDates.forEach(date => {
              const exists = weekSlots.find(s => s.location === loc && s.dates === date);
              if (!exists) {
                // Variant wasn't returned by API — assume sold out
                weekSlots.push({
                  variantId: null,
                  location: loc,
                  dates: date,
                  price: defaultPrice,
                  quantityAvailable: 0,
                  availableForSale: false,
                  status: 'sold_out'
                });
              }
            });
          });

          return {
            id: node.id,
            title: node.title,
            description: node.description,
            shortDescription: shortDescription || node.description,
            tags: node.tags || [],
            handle: node.handle,
            imageUrl: node.featuredImage?.url,
            imageAlt: node.featuredImage?.altText || node.title,
            variants,
            weekSlots,
            metafieldWeeks: weekAvailability
          };
        });

      // Filter to only "summer camp" tagged products
      const summerCamps = products.filter(product => {
        const productTags = product.tags.map(tag => tag.toLowerCase());
        return productTags.includes('summer camp');
      });

      // Extract unique dates (weeks) sorted chronologically
      const allDates = [...new Set(
        summerCamps.flatMap(p => p.weekSlots.map(s => s.dates).filter(Boolean))
      )].sort((a, b) => {
        // Sort by parsing first date in range
        const parseDate = (str) => {
          if (!str) return 0;
          const monthMap = { 'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5, 'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11, 'june': 5, 'july': 6 };
          const lower = str.toLowerCase();
          for (const [name, num] of Object.entries(monthMap)) {
            if (lower.includes(name)) {
              const dayMatch = lower.match(/(\d+)/);
              const day = dayMatch ? parseInt(dayMatch[1]) : 1;
              return num * 100 + day;
            }
          }
          return 0;
        };
        return parseDate(a) - parseDate(b);
      });

      // Extract unique locations
      const allLocations = [...new Set(
        summerCamps.flatMap(p => p.weekSlots.map(s => s.location).filter(Boolean))
      )].sort();

      setCamps(summerCamps);
      setWeeks(allDates);
      setLocations(allLocations);
    } catch (err) {
      console.error('API Error:', err);
      setError(`Error fetching camp data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCamps();
  }, []);

  useEffect(() => {
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'setHeight', height }, '*');
    };
    sendHeight();
    const resizeObserver = new ResizeObserver(() => sendHeight());
    resizeObserver.observe(document.body);
    window.addEventListener('resize', sendHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);

  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <ThemeProvider theme={theme}>
      <Routes>
        <Route path="/" element={
          isMobile ?
          <Navigate to="/mobile" replace /> :
          <Home
            camps={camps}
            weeks={weeks}
            locations={locations}
            loading={loading}
            error={error}
            fetchCamps={fetchCamps}
          />
        } />
        <Route path="/camp/:handle" element={<CampDetails camps={camps} locations={locations} />} />
        <Route path="/mobile" element={
          <MobileCampView
            camps={camps}
            locations={locations}
            loading={loading}
            error={error}
            fetchCamps={fetchCamps}
          />
        } />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
